import { z } from "zod";
import {
  CaseResultSchema,
  CaseStatusSchema,
  CompanyIdQuerySchema,
  IdSchema,
  IsoDateOrDateTimeSchema,
  IsoDateSchema,
  IsoDateTimeSchema,
  PaginationQuerySchema,
  QueryBooleanSchema,
} from "./common.js";

/**
 * 진실의 원천: harness/API-SPEC.md §2, harness/RMA-PROCEDURE.md, apps/api/migrations/0001_init.sql (cases)
 */

export const CaseDetailSchema = z.object({
  id: IdSchema,
  case_no: z.string(),
  serial_norm: z.string(),
  owner_company_id: IdSchema,
  received_at: IsoDateSchema,
  // case.pii 권한이 없으면 서버가 null 로 마스킹해 내려준다 (harness/SECURITY.md §2)
  sender_name: z.string().nullable(),
  sender_contact: z.string().nullable(),
  reported_symptom: z.string().nullable(),
  our_finding: z.string().nullable(),
  status: CaseStatusSchema,
  result: CaseResultSchema,
  intake_batch_id: IdSchema.nullable(),
  reentry_of_case_id: IdSchema.nullable(),
  /**
   * 재입고로 이어진 **이전 케이스의 번호**.
   *
   * 사람은 `#311` 이 아니라 `RMA-2026-0300` 으로 케이스를 부른다. 화면이 내부 id 를
   * 보여 주면, 그 번호를 어디에도 검색할 수 없는 사람은 링크를 눌러 보기 전까지
   * 무엇인지 알 수 없다 — **화면이 사람의 언어로 말하지 않는 것**이다.
   */
  reentry_of_case_no: z.string().nullable(),
  closed_at: IsoDateTimeSchema.nullable(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
  // ★ 이 프로젝트의 제1 목적. 케이스를 보는 사람이 교체 시리얼을 **여기서** 봐야 한다 —
  // 다른 화면으로 건너가야 보이면 그 연결은 실무에서 끊긴 것과 같다.
  replacement_serial: z.string().nullable(),
  secui_case_no: z.string().nullable(),
  swapped_at: IsoDateOrDateTimeSchema.nullable(),
  /**
   * 마지막 출고 — 날짜와 받는 회사. 목록에 입고일만 있고 출고일이 없으면 "지금 어디 있나"
   * 를 한 줄에서 못 읽는다 (2026-09-09 대표 지시). 출고가 없으면 둘 다 null
   */
  shipped_at: IsoDateSchema.nullable(),
  shipped_to: z.string().nullable(),
});
export type CaseDetail = z.infer<typeof CaseDetailSchema>;

export const CaseListQuerySchema = PaginationQuerySchema.extend({
  status: CaseStatusSchema.optional(),
  company: CompanyIdQuerySchema,
  serial: z.string().optional(),
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
  /**
   * 승인 대기 큐 — 아직 **승인도 출고도 없는** 건.
   * 승인은 났는데 출고만 안 한 건은 승인 대기가 아니라 출고 대기다.
   */
  approval_pending: QueryBooleanSchema.optional(),
  /**
   * ★ 교체 시리얼 유무. `none` 은 "SECUI 에 가 있는데 아직 교체품이 안 온 건" 을
   * 고르는 조건이다 — 이 프로젝트의 작업 큐가 그것이다.
   */
  swap: z.enum(["any", "none"]).optional(),
  // 케이스 목록의 커서는 마지막으로 본 case.id 다. 형식을 여기서 막지 않으면
  // 깨진 커서가 "결과 없음"으로 조용히 내려간다 — 빈 결과를 성공으로 보고하지
  // 않는다는 이 프로젝트의 원칙에 어긋난다(반증 RT-8).
  cursor: z.string().regex(/^\d+$/, "커서 형식이 올바르지 않습니다").optional(),
});
export type CaseListQuery = z.infer<typeof CaseListQuerySchema>;

export const CaseListResponseSchema = z.object({
  items: z.array(CaseDetailSchema),
  next_cursor: z.string().nullable(),
  // 조건에 맞는 **전체** 건수. 화면이 items.length 로 세면 limit 에서 잘린 수를
  // 전체인 양 보여준다 — 잘린 숫자를 답인 것처럼 보여주지 않는다
  total: z.number().int().nonnegative(),
});
export type CaseListResponse = z.infer<typeof CaseListResponseSchema>;

/** 한 번에 접수할 수 있는 시리얼 수 상한 (harness/API-SPEC.md §2-1 규칙 8) */
export const INTAKE_MAX_SERIALS = 200;

// 배치 입고 — 시리얼 여러 줄 붙여넣기 (harness/UI-SPEC.md §1 "케이스" 화면)
export const CaseBatchIntakeRequestSchema = z.object({
  received_at: IsoDateSchema,
  owner_company_id: IdSchema,
  sender_name: z.string().nullable().optional(),
  sender_contact: z.string().nullable().optional(),
  memo: z.string().nullable().optional(),
  // 상한이 있다. 한 요청이 감당할 수 있는 양을 넘으면 도중에 죽는데, 그때
  // 케이스는 일부만 만들어지고 응답은 오지 않는다(반증 RT-9). 조용히 반쯤
  // 접수되느니 분명하게 거절하고 나눠 넣게 한다. 실측 최대치는 한 배치 64건이다.
  /**
   * 접수할 줄. **시리얼 하나에 증상 하나**까지 같이 받는다.
   *
   * 기존 엑셀은 한 행에 시리얼과 `비고`(증상)가 나란히 있었다. 접수와 증상 기록을
   * 갈라 놓으면, 20대를 받은 사람은 접수 한 번 뒤에 케이스를 20번 열어야 한다 —
   * 그 순간 사람은 엑셀로 돌아간다. 증상은 **선택**이다(모르면 비워 둔다, S2).
   */
  items: z
    .array(
      z.object({
        serial: z.string().min(1),
        reported_symptom: z.string().nullable().optional(),
      }),
    )
    .min(1)
    .max(INTAKE_MAX_SERIALS),
});
export type CaseBatchIntakeRequest = z.infer<typeof CaseBatchIntakeRequestSchema>;

// 붙여넣기에는 중복 줄과 빈 줄이 섞인다. 접수를 통째로 되돌리지 않고,
// 무엇을 접수했고 무엇을 건너뛰었는지 둘 다 돌려준 뒤 사람이 판단한다
// (harness/API-SPEC.md §2-1 규칙 4·5·7, SOUL S1·S5).
export const INTAKE_SKIP_REASONS = ["DUPLICATE_IN_BATCH", "EMPTY_AFTER_NORMALIZE"] as const;

export const CaseIntakeSkipSchema = z.object({
  serial_raw: z.string(),
  reason: z.enum(INTAKE_SKIP_REASONS),
});
export type CaseIntakeSkip = z.infer<typeof CaseIntakeSkipSchema>;

export const CaseBatchIntakeResponseSchema = z.object({
  intake_batch_id: IdSchema,
  created: z.array(CaseDetailSchema),
  // 재입고 감지(harness/DATA-DICTIONARY.md NR-04) — 막지 않고 알려주기만 한다
  reentry_warnings: z.array(
    z.object({ serial_norm: z.string(), previous_case_id: IdSchema }),
  ),
  /**
   * 말이 되지 않는 값에 대한 경고. **접수는 그대로 된다**(SOUL S1).
   *
   * 실측: `2026` 을 `2062` 로 잘못 쳐서 입고일이 36년 뒤가 되어도 201 이었고,
   * 케이스 번호까지 `RMA-2062-0001` 로 발번됐다 — 그 해의 번호대가 하나 열린다.
   * 막지는 않는다. 다만 **조용히 넘어가지도 않는다**(S2·S5).
   */
  warnings: z.array(z.string()),
  skipped: z.array(CaseIntakeSkipSchema),
});
export type CaseBatchIntakeResponse = z.infer<typeof CaseBatchIntakeResponseSchema>;

/**
 * 검수 기록 (harness/API-SPEC.md §2-2).
 * 상태 전이는 막지 않는다 — 비정상 이동도 저장되고, 확인은 화면이 묻는다
 * (harness/RMA-PROCEDURE.md §3, SOUL S1).
 * `result` 의 `null` 은 "미정"이고 유효한 값이다 — 임의로 채우지 않는다(S2).
 */
export const CaseUpdateRequestSchema = z
  .object({
    reported_symptom: z.string().nullable().optional(),
    our_finding: z.string().nullable().optional(),
    status: CaseStatusSchema.optional(),
    result: CaseResultSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: "바꿀 항목을 하나 이상 보내야 합니다.",
  });
export type CaseUpdateRequest = z.infer<typeof CaseUpdateRequestSchema>;

/**
 * 케이스 삭제 (harness/API-SPEC.md §2-3).
 * 삭제는 부분 성공이다 — 지운 것과 못 지운 것을 둘 다 돌려주고 사람이 판단한다(SOUL S5).
 */
export const CaseDeleteRequestSchema = z.object({
  case_ids: z.array(IdSchema).min(1).max(200),
  // 왜 지웠는지 없이 지운 기록은 나중에 아무것도 설명하지 못한다
  reason: z.string().trim().min(2),
});
export type CaseDeleteRequest = z.infer<typeof CaseDeleteRequestSchema>;

export const CaseDeleteResponseSchema = z.object({
  deleted: z.array(IdSchema),
  /**
   * `case_no` 는 **볼 수 있는 케이스일 때만** 채운다. 없는 번호와 스코프 밖 번호를
   * 구분해 말하지 않는 규칙(§2)이 여기에도 걸린다 — 번호를 돌려주면 "그 케이스가
   * 있다" 는 사실이 새기 때문이다. 화면은 번호가 없으면 내부 id 를 그대로 쓴다.
   */
  blocked: z.array(
    z.object({
      case_id: z.number().int(),
      case_no: z.string().nullable(),
      reason: z.string(),
    }),
  ),
});
export type CaseDeleteResponse = z.infer<typeof CaseDeleteResponseSchema>;

/**
 * 자주 쓴 증상 (harness/API-SPEC.md §2-4).
 *
 * 지어낸 목록이 아니라 **이미 적힌 것을 세어** 돌려준다. 실제 데이터에서
 * "셋팅완료 2.0.12" 가 10번, "전원인가 X" 가 3번 나온다 — 같은 문장을 열 번 치는 것은
 * 사람이 할 일이 아니다. 채워 넣기만 하고 **판단은 사람이 한다**(SOUL S5).
 */
export const CaseSymptomsResponseSchema = z.object({
  items: z.array(z.object({ text: z.string(), count: z.number().int().positive() })),
});
export type CaseSymptomsResponse = z.infer<typeof CaseSymptomsResponseSchema>;
